import { KatapultElement, html } from '../../mixins/katapult-element.js';
import { KatapultEditorTemplate } from './katapult-editor-template.js';
import { html as litHtml } from 'lit';
import '@polymer/paper-toast/paper-toast.js';
import '../input-element/input-element.js';

import { PortalStyle } from './katapult-model-editor-style.js';

import '../paper-header/paper-header.js';
import { getVersions } from '../../modules/IPLVersionManager.js';
import { KatapultDialog } from '../katapult-elements/katapult-dialog.js';
import { CamelCase } from '../../modules/CamelCase.js';

customElements.define(
  'katapult-default-spec-editor',
  class extends KatapultEditorTemplate {
    static get template() {
      return html`
        ${super.style} ${PortalStyle}
        <style>
          paper-cell[icon],
          paper-cell[icon-button] {
            height: 50px;
          }
          .nameCell {
            max-width: 400px;
            --paper-input-container: {
              padding: 0;
            };
          }
          #title {
            text-transform: none;
            font-size: 14pt;
            color: var(--paper-grey-600);
            padding-left: 0;
            flex-grow: 0;
            flex-basis: auto;
          }
          #dot {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 8px;
            width: 8px;
            font-size: 0;
            background: var(--paper-grey-400);
            color: white;
            border-radius: 100%;
            flex-shrink: 0;
            transition: all 0.3s;
          }
        </style>

        <paper-toast id="toast"></paper-toast>

        <katapult-firebase-worker path="[[modelSpaceDetails.path]]/models/attributes" data="{{otherAttributes}}"></katapult-firebase-worker>

        <katapult-firebase-worker
          path="photoheight/job_permissions/[[userGroup]]/list"
          data="{{jobPermissions}}"
        ></katapult-firebase-worker>

        <katapult-firebase-worker path="[[modelSpaceDetails.path]]/models/export_models" data="{{exportModels}}"></katapult-firebase-worker>

        <paper-table>
          <paper-row expand>
            <paper-cell icon>
              <div id="dot"></div>
            </paper-cell>
            <paper-cell id="title">Integrated Pole Loading</paper-cell>
          </paper-row>
          <template is="dom-if" if="[[IPLVersions.length]]">
            <!-- Engine Version -->
            <paper-row input>
              <paper-cell icon>
                <material-icon icon="manage_history"></material-icon>
              </paper-cell>
              <paper-cell shrink>Default Pole Loading Engine Version</paper-cell>
              <paper-cell style="max-width: 600px;">
                <katapult-drop-down
                  value="[[_itemData.engine_version]]"
                  items="[[IPLVersions]]"
                  value-path="version"
                  label-path="version"
                  secondary-label-path="description"
                  data-path="_itemData.engine_version"
                  on-selected-changed="updateProperty"
                  no-label-float
                  grow
                  hide-attribute-name
                >
                </katapult-drop-down>
              </paper-cell>
              <paper-cell italic>Which engine version should be default?</paper-cell>
            </paper-row>
          </template>
          <!-- Soil Type -->
          <paper-row input>
            <paper-cell icon>
              <material-icon icon="grass"></material-icon>
            </paper-cell>
            <paper-cell shrink>Default Soil Type</paper-cell>
            <paper-cell style="max-width: 600px;">
              <input-element
                model="[[otherAttributes.soil_type]]"
                other-attributes="[[otherAttributes]]"
                value="[[_itemData.soil_type]]"
                data-path="_itemData.soil_type"
                on-input-element-change="updateProperty"
                no-label-float
                grow
                hide-attribute-name
              >
              </input-element>
            </paper-cell>
            <paper-cell italic>Which soil type should be default?</paper-cell>
          </paper-row>
          <!-- Wind Angular Resolution -->
          <paper-row input>
            <paper-cell icon>
              <material-icon icon="flip_camera_android"></material-icon>
            </paper-cell>
            <paper-cell shrink>Wind Angular Resolution</paper-cell>
            <paper-cell style="max-width: 100px;">
              <paper-input
                type="number"
                step="1"
                value="[[_itemData.wind_angular_resolution]]"
                data-path="_itemData.wind_angular_resolution"
                on-value-changed="updateProperty"
                no-label-float
                grow
              >
                <span slot="suffix">°</span>
              </paper-input>
            </paper-cell>
            <paper-cell italic>How regularly should wind direction be sampled?</paper-cell>
          </paper-row>
          <paper-row expand>
            <paper-cell italic
              >Angular wind resolution comes with a speed-accuracy tradeoff. Finer (smaller) values provide more accurate pole loading
              results, but will run slower.</paper-cell
            >
          </paper-row>
          <!-- Additional Standard Load Cases -->
          <paper-row expand>
            <paper-cell icon>
              <material-icon icon="air"></material-icon>
            </paper-cell>
            <paper-cell shrink column align-start style="max-width: 400px;">Additional Load Cases</paper-cell>
            <paper-table rounded outline grow dividers overflow-hidden>
              <paper-row top-rounded short grey>
                <paper-cell>Load Case</paper-cell>
                <paper-cell icon></paper-cell>
              </paper-row>
              <paper-table-scroll>
                <template is="dom-repeat" items="{{_itemData.custom_load_cases}}">
                  <paper-header-content
                    white
                    opened="{{item.$opened}}"
                    no-click-row
                    slot="sortable"
                    divider
                    data-value="$true"
                    on-opened-changed="updateProperty"
                  >
                    <!--HEADER ROW-->
                    <paper-cell slot="header">
                      <katapult-drop-down
                        label="Select a Load Case"
                        items="[[getPicklists(otherAttributes.load_case.picklists)]]"
                        label-function="[[camelCase]]"
                        value="[[item.value]]"
                        no-label-float
                        on-selected-changed="loadCaseSelected"
                      ></katapult-drop-down>
                    </paper-cell>
                    <paper-cell icon-button slot="header-end" on-click="deleteLoadCaseTableItem">
                      <katapult-button iconOnly noBorder icon="delete"></katapult-button>
                    </paper-cell>

                    <!--CONTENT ROW-->
                    <div style="background: var(--paper-grey-50); padding: 24px;" slot="content">
                      <div flex column style="gap: 24px;">
                        <!-- Filter -->
                        <div flex column style="gap: 8px;">
                          <div flex align-center style="gap: 8px;">
                            <material-icon icon="filter_alt"></material-icon>
                            <div style="font-size: 16px; font-weight: 500;">Filter (optional)</div>
                          </div>
                          <k-logic-editor
                            k-logic-type="LOAD_CASE_FILTER"
                            debug-reference-data="[[debugReferenceData]]"
                            block="{{item.filter}}"
                          ></k-logic-editor>
                        </div>
                      </div>
                    </div>
                  </paper-header-content>
                </template>
              </paper-table-scroll>
              <paper-row ripple short bottom-rounded on-click="addLoadCaseTableItem">
                <paper-cell center pointer style="color: var(--paper-grey-600);">
                  <span>ADD LOAD CASE</span>
                  <iron-icon style="margin-left: 8px;" icon="add-circle-outline"></iron-icon>
                </paper-cell>
              </paper-row>
            </paper-table>
          </paper-row>
          <paper-row expand>
            <paper-cell italic>
              Additional Load Cases are automatically analyzed if there is at least one Load Case attribute on a pole.
            </paper-cell>
          </paper-row>
          <!-- Default Attributes -->
          <paper-row input expand>
            <paper-cell icon>
              <material-icon icon="list"></material-icon>
            </paper-cell>
            <paper-cell shrink>Default Attributes</paper-cell>
            <paper-cell italic>Which attributes should be set by default when bulk pole loading (leave blank if none)?</paper-cell>
          </paper-row>
          <!-- Pass -->
          <paper-row style="width:600px; margin-left:125px;">
            <paper-cell style="max-width:100px; margin-top:20px;">Pass</paper-cell>
            <paper-cell input>
              <katapult-drop-down
                label="Attribute"
                items="[[getNodeAttributes(otherAttributes)]]"
                value="[[_itemData.bulk_loading.pass_attribute]]"
                label-path="label"
                value-path="value"
                data-path="_itemData.bulk_loading.pass_attribute"
                on-selected-changed="updateProperty"
              ></katapult-drop-down>
            </paper-cell>
            <paper-cell input>
              <input-element
                model="[[getSub(otherAttributes, _itemData.bulk_loading.pass_attribute)]]"
                other-attributes="[[otherAttributes]]"
                value="[[_itemData.bulk_loading.pass_value]]"
                data-path="_itemData.bulk_loading.pass_value"
                on-input-element-change="updateProperty"
                label="Value"
                grow
                hide-attribute-name
              >
              </input-element>
            </paper-cell>
          </paper-row>
          <!-- Fail -->
          <paper-row style="width:600px; margin-left:125px;">
            <paper-cell style="max-width:100px; margin-top:20px;">Failure</paper-cell>
            <paper-cell input>
              <katapult-drop-down
                label="Attribute"
                items="[[getNodeAttributes(otherAttributes)]]"
                value="[[_itemData.bulk_loading.fail_attribute]]"
                label-path="label"
                value-path="value"
                data-path="_itemData.bulk_loading.fail_attribute"
                on-selected-changed="updateProperty"
              ></katapult-drop-down>
            </paper-cell>
            <paper-cell input>
              <input-element
                model="[[getSub(otherAttributes, _itemData.bulk_loading.fail_attribute)]]"
                other-attributes="[[otherAttributes]]"
                value="[[_itemData.bulk_loading.fail_value]]"
                data-path="_itemData.bulk_loading.fail_value"
                on-input-element-change="updateProperty"
                label="Value"
                grow
                hide-attribute-name
              >
              </input-element>
            </paper-cell>
          </paper-row>

          <template is="dom-if" if="[[exportModels.spida]]">
            <!-- Spida Defaults -->
            <template is="dom-if" if="[[!poleLoadingExportDisabled(exportModels,'spida')]]">
              <paper-row expand style="margin-top:50px">
                <paper-cell icon>
                  <div id="dot"></div>
                </paper-cell>
                <paper-cell id="title">SPIDA Defaults</paper-cell>
              </paper-row>
              <paper-row expand>
                <paper-cell icon>
                  <material-icon icon="cable"></material-icon>
                </paper-cell>
                <paper-cell shrink column align-start style="max-width: 400px;">Cable Types to Export as Messengers Usage Group</paper-cell>
                <paper-cell>
                  <paper-table rounded outline grow dividers overflow-hidden>
                    <paper-row top-rounded short grey>
                      <paper-cell>Cable Type</paper-cell>
                      <paper-cell icon></paper-cell>
                    </paper-row>
                    <paper-table-scroll>
                      <template is="dom-repeat" items="{{_itemData.spida.cables_as_messenger}}">
                        <paper-row white slot="sortable" divider>
                          <paper-cell>
                            <katapult-drop-down
                              label="Select a Cable Type"
                              items="[[getPicklists(otherAttributes.cable_type.picklists)]]"
                              label-function="[[camelCase]]"
                              value="[[item.value]]"
                              no-label-float
                              on-selected-changed="cableTypeSelected"
                            >
                            </katapult-drop-down>
                          </paper-cell>
                          <paper-cell icon-button on-click="deleteCableTypeTableItem">
                            <katapult-button iconOnly noBorder icon="delete"></katapult-button>
                          </paper-cell>
                        </paper-row>
                      </template>
                    </paper-table-scroll>
                    <paper-row id="spida" ripple short bottom-rounded on-click="addCableTypeTableItem">
                      <paper-cell center pointer style="color: var(--paper-grey-600);">
                        <span>ADD CABLE TYPE</span>
                        <iron-icon style="margin-left: 8px;" icon="add-circle-outline"></iron-icon>
                      </paper-cell>
                    </paper-row>
                  </paper-table>
                </paper-cell>
              </paper-row>
            </template>
            <!-- OCalc Defaults -->
            <template is="dom-if" if="[[!poleLoadingExportDisabled(exportModels,'ocalc')]]">
              <paper-row expand style="margin-top:50px">
                <paper-cell icon>
                  <div id="dot"></div>
                </paper-cell>
                <paper-cell id="title">O-Calc Defaults</paper-cell>
              </paper-row>
              <paper-row expand>
                <paper-cell icon>
                  <material-icon icon="cable"></material-icon>
                </paper-cell>
                <paper-cell shrink column align-start style="max-width: 400px;"
                  >Cable Types will be mapped to selected O-Calc Span Type</paper-cell
                >
                <paper-cell>
                  <paper-table rounded outline grow dividers overflow-hidden>
                    <paper-row top-rounded short grey>
                      <paper-cell>Cable Type</paper-cell>
                      <paper-cell>O-Calc Span Type</paper-cell>
                      <paper-cell icon></paper-cell>
                    </paper-row>
                    <paper-table-scroll>
                      <template is="dom-repeat" items="{{_itemData.ocalc.span_type_mapping}}">
                        <paper-row white slot="sortable" divider>
                          <paper-cell>
                            <katapult-drop-down
                              label="Select a Cable Type"
                              items="[[getPicklists(otherAttributes.cable_type.picklists)]]"
                              label-function="[[camelCase]]"
                              value="[[item.cable_type]]"
                              no-label-float
                              on-selected-changed="ocalcCableTypeSelected"
                            >
                            </katapult-drop-down>
                          </paper-cell>
                          <paper-cell>
                            <katapult-drop-down
                              label="Select a Span Type"
                              items="[[ocalcSpanTypes]]"
                              label-function="[[camelCase]]"
                              value="[[item.span_type]]"
                              no-label-float
                              on-selected-changed="spanTypeSelected"
                            >
                            </katapult-drop-down>
                          </paper-cell>
                          <paper-cell icon-button on-click="deleteSpanTypeTableItem">
                            <katapult-button iconOnly noBorder icon="delete"></katapult-button>
                          </paper-cell>
                        </paper-row>
                      </template>
                    </paper-table-scroll>
                    <paper-row id="ocalc" ripple short bottom-rounded on-click="addCableTypeTableItem">
                      <paper-cell center pointer style="color: var(--paper-grey-600);">
                        <span>ADD CABLE TYPE</span>
                        <iron-icon style="margin-left: 8px;" icon="add-circle-outline"></iron-icon>
                      </paper-cell>
                    </paper-row>
                  </paper-table>
                </paper-cell>
              </paper-row>
            </template>
          </template>
        </paper-table>
      `;
    }

    ready() {
      super.ready();
      try {
        getVersions((versions) => (this.IPLVersions = Object.values(versions ?? {})));
      } catch (e) {
        console.error(e);
      }
    }
    static get properties() {
      return {
        ocalcSpanTypes: {
          type: Array,
          value: () => ['Primary', 'Secondary', 'Service', 'Neutral', 'Telco', 'Fiber', 'CATV', 'Sub-Transmission', 'Other', 'Unknown']
        }
      };
    }
    static get observers() {
      return ['setKLogicDebugReferenceData(jobPermissions)'];
    }
    connectedCallback() {
      super.connectedCallback();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
    }

    addLoadCaseTableItem() {
      if (!this.get('_itemData')) this.set('_itemData', {});
      if (Array.isArray(this.get('_itemData.custom_load_cases'))) this.push('_itemData.custom_load_cases', { display: true });
      else this.set('_itemData.custom_load_cases', [{ display: true }]);
    }
    loadCaseSelected(e) {
      const value = e.detail.selectedItem;
      this.set(`_itemData.custom_load_cases.${e.model.index}.value`, value);
    }
    deleteLoadCaseTableItem(e) {
      let index = e.model.index;
      this.splice(`_itemData.custom_load_cases`, index, 1);
    }

    addCableTypeTableItem(e) {
      const plaExport = e.currentTarget.id;
      if (!this.get('_itemData')) this.set('_itemData', {});
      if (!this.get(`_itemData.${plaExport}`)) this.set(`_itemData.${plaExport}`, {});
      if (plaExport === 'spida') {
        if (Array.isArray(this.get('_itemData.spida.cables_as_messenger')))
          this.push('_itemData.spida.cables_as_messenger', { display: true });
        else this.set('_itemData.spida.cables_as_messenger', [{ display: true }]);
      } else if (plaExport === 'ocalc') {
        if (Array.isArray(this.get('_itemData.ocalc.span_type_mapping'))) this.push('_itemData.ocalc.span_type_mapping', { display: true });
        else this.set('_itemData.ocalc.span_type_mapping', [{ display: true }]);
      }
    }
    getPicklists(picklists) {
      const list = [];
      for (const picklist in picklists) {
        for (const value in picklists[picklist]) {
          list.push(picklists[picklist][value].value);
        }
      }
      return list;
    }
    cableTypeSelected(e) {
      let value = e.detail.selectedItem;
      this.set(`_itemData.spida.cables_as_messenger.${e.model.index}.value`, value);
    }
    ocalcCableTypeSelected(e) {
      let value = e.detail.selectedItem;
      this.set(`_itemData.ocalc.span_type_mapping.${e.model.index}.cable_type`, value);
    }
    spanTypeSelected(e) {
      let value = e.detail.selectedItem;
      this.set(`_itemData.ocalc.span_type_mapping.${e.model.index}.span_type`, value);
    }
    deleteCableTypeTableItem(e) {
      let index = e.model.index;
      this.splice('_itemData.spida.cables_as_messenger', index, 1);
    }
    deleteSpanTypeTableItem(e) {
      let index = e.model.index;
      this.splice('_itemData.ocalc.span_type_mapping', index, 1);
    }

    /**
     * This method is called before the item is saved to the database
     * It checks if the wind angular resolution is within the bounds and clips it if it is not
     * It also warns the user that changing the values will affect integrated pole loading behavior
     */
    async beforeSave() {
      const currentWindAngularResolution = parseFloat(this._itemData.wind_angular_resolution);

      // Create a dialog to notify the user that the value is out of bounds and will be clipped
      let clippedWindAngularResolution = currentWindAngularResolution;
      const upperLim = 45; // Highest value allowed for wind angular resolution [°]
      const lowerLim = 1; // Lowest value allowed for wind angular resolution [°]
      if (currentWindAngularResolution < lowerLim || currentWindAngularResolution > upperLim) {
        clippedWindAngularResolution = Math.min(upperLim, Math.max(lowerLim, currentWindAngularResolution));
        const clippedDialog = this._openClippingDialog(currentWindAngularResolution, clippedWindAngularResolution);
        await clippedDialog.closeStart;
      }
      // Create a dialog when the user tries to save to warn that the values will change pole loading behavior
      // Give the option to continue or cancel
      const confirmDialog = this._openChangeConfirmDialog();
      const confirmedVal = await confirmDialog.confirmed;
      if (!confirmedVal) {
        // Pop up toast to notify the user that the save was cancelled
        this.toast('Save cancelled', 5000);
        throw new Error('User cancelled save');
      }
      if (!isNaN(clippedWindAngularResolution)) this._itemData.wind_angular_resolution = clippedWindAngularResolution;
    }

    getNodeAttributes(otherAttributes) {
      return Object.keys(otherAttributes ?? {})
        .filter((key) => otherAttributes[key].attribute_types?.includes('node'))
        .map((x) => ({ value: x, label: CamelCase(x) }));
    }

    /**
     * Opens a dialog to notify the user that the wind angular resolution
     *  is out of bounds [1°, 45°] and will be clipped
     * @param {number} windAngularResolution Wind Angular Resolution value before clipping [°]
     * @param {number} clippedWindAngularResolution Clipped Wind Angular Resolution value [°]
     * @returns {KatapultDialog} - The dialog that was opened
     */
    _openClippingDialog(windAngularResolution, clippedWindAngularResolution) {
      return KatapultDialog.alert(
        `Wind Angular Resolution of ${windAngularResolution}° is out of bounds and will be clipped to ${clippedWindAngularResolution}°.`,
        'Clipping Wind Angular Resolution'
      );
    }

    /**
     * Opens a dialog to notify the user that changing the values will affect integrated pole loading
     * @returns {KatapultDialog} - The dialog that was opened
     */
    _openChangeConfirmDialog() {
      return KatapultDialog.confirm({
        dialog: {
          title: 'Changing Integrated Pole Loading Behavior',
          modal: true
        },
        body: 'Changing these values will affect integrated pole loading behavior. Are you sure you want to continue?'
      });
    }

    /**
     * This method displays a given message in a toast for the given duration
     * @param {String} toastMessage - The message to display in the toast
     * @param {Number} duration - The duration to display the toast
     */
    toast(toastMessage, duration) {
      let toastElement = this.shadowRoot.getElementById('toast');
      if (toastElement) toastElement.show({ text: toastMessage, duration });
    }

    setKLogicDebugReferenceData() {
      if (this.jobPermissions == null) return;
      this.debugReferenceData = { jobs: this.jobPermissions };
    }

    poleLoadingExportDisabled(exportModels, key) {
      if (!exportModels) return true;
      const poleLoadingExportModel = exportModels[key];
      if (!poleLoadingExportModel || poleLoadingExportModel._disabled) return true;
      if (key == 'dds' && poleLoadingExportModel.utility_name == null) return true;
      return false;
    }
  }
);
